import { track } from '@amplitude/analytics-browser'

import { getClientEnv } from '@/env/client'
import { readSnowplowIds } from '@/services/snowplow/read-ids'
import type { SnowplowIds } from '@/services/wapi'

interface TrackInput {
    email: string
    locale: string
    originCity: string
    destinationCity: string
    transportMode: string
    /**
     * Where the signup occurred, e.g. `landing_c2c`. One value, two destinations:
     * Amplitude's `subscription_source` and — forwarded verbatim by the endpoint —
     * the WAPI event's `signup_source`.
     */
    subscriptionSource: string
}

declare global {
    interface Window {
        dataLayer?: unknown[]
    }
}

function pushDataLayer(email: string) {
    if (typeof window === 'undefined') {
        return
    }
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({ user_data: { email } })
}

/**
 * Amplitude `Newsletter Subscribed`, per the strategy data dictionary
 * (amplitude-events-post-booking.md). The legacy snake_case
 * `email_newsletter_subscribed` (copied from ui-react) is dictionary-managed
 * as renamed to this event. The SnowplowIdsPlugin already enriches with
 * domain_user_id / network_user_id.
 */
function trackAmplitude(input: TrackInput) {
    // track() is a safe no-op (never throws) when the SDK isn't initialized —
    // e.g. an ad-blocker blocked it — so this call needs no guard or try/catch.
    track('Newsletter Subscribed', {
        arrive_city: input.destinationCity,
        depart_city: input.originCity,
        email_address: input.email,
        mode_type: input.transportMode,
        subscription_source: input.subscriptionSource,
    })
}

async function submitToServer(
    input: TrackInput,
    snowplowIds: SnowplowIds,
): Promise<{ ok: boolean }> {
    try {
        const response = await fetch('/api/nexus/newsletter-subscriptions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...input, snowplowIds }),
        })
        return { ok: response.ok }
    } catch (error) {
        getClientEnv()
            .logger.withMetadata({ error: error instanceof Error ? error.message : String(error) })
            .warn('Newsletter subscription POST failed')
        return { ok: false }
    }
}

/**
 * Fires the three-action newsletter subscription pipeline:
 * - GTM dataLayer push (defensive — works before GTM loads; see nexus#244)
 * - Amplitude `email_newsletter_subscribed` (copied from ui-react)
 * - POST to /api/nexus/newsletter-subscriptions -> WAPI -> Kafka -> messaging -> Blueshift
 *
 * Returns { ok } based only on the server submission. Amplitude + dataLayer
 * are fire-and-forget: marketing attribution is valuable even on transient
 * API failures.
 */
export async function trackNewsletterSubscription(input: TrackInput): Promise<{ ok: boolean }> {
    pushDataLayer(input.email)
    trackAmplitude(input)
    const snowplowIds = readSnowplowIds()
    return submitToServer(input, snowplowIds)
}
